import {
  useMemo,
  useEffect,
  useRef,
  useState,
} from "react";

import { useGameContext } from "GameContext";
import { useChatContext, type MessageType} from "ChatContext";

export default function ChatRoom({
  onBackClick=()=>null,
  roomId,
} : {
  onBackClick?: () => void,
  roomId: string,
}) {
  const { chatRooms, sendMessage } = useChatContext();
  const { playerInfo, playerMap } = useGameContext() as any;

  const listEndRef = useRef<HTMLDivElement | null>(null);
  const divRef = useRef<HTMLDivElement | null>(null);
  const [isAtEnd, setIsAtEnd] = useState(false);

  useEffect(() => {
    const div = divRef.current;
    if (!div) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = div;
      const atEnd = scrollTop + clientHeight >= scrollHeight - 10; // -1 for floating point safety
      setIsAtEnd(atEnd);
    };

    div.addEventListener("scroll", handleScroll);
    return () => div.removeEventListener("scroll", handleScroll);
  }, []);

  const [message, setMessage] = useState("");

  function onSendMessage() {
    const newMessage = message.trim();

    if (!newMessage) {
      setMessage("");
      return
    }

    sendMessage(roomId, {
      author: playerInfo,
      msg: newMessage,
      date: new Date()
    })

    setMessage("");
  }

  const chatRoom = roomId ? chatRooms.get(roomId) : null;
  const messages = chatRoom?.messages ?? []
  const messageCount = messages.length

  const roomName = useMemo(() => {
    if (roomId === "everyone") {
      return "General"
    }

    const [est, roleOrSm] = roomId.split("-")

    if (roleOrSm === "estate") {
      return est === "public" ? "Public Estates" : "Private Estates";
    }

    if (roleOrSm) {
      const epe =  est === "public" ? "Public" : "Private";
      return epe + " " + roleOrSm
    }
    return ""
  }, [roomId])

  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: "smooth"})
  }, [])

  useEffect(() => {
    if (isAtEnd) {
      listEndRef.current?.scrollIntoView({ behavior: "smooth"})
    }
  }, [messageCount, isAtEnd])

  return (
    <div className="w-full h-full bg-[#a19881] p-2 flex flex-col gap-2 border-2 border-black">
      <button onClick={onBackClick} className="absolute top-0 right-0 p-2">x</button>

      { roomName ? (
        <p className="text-sm">{`${roomName} Chat`}</p>
      ) : (
        <div className="text-sm text-nowrap flex flex-row overflow-x-auto gap-2 items-center max-w-[calc(100%-10px)]">
          { chatRoom?.participants.map((participantId: number) => {
            const participant = playerMap?.get(participantId);
            if (!participant) {
              return null;
            }
            return (
              <p key={participantId}>{participant.playerName}-({participant.estate} {participant.role})</p>
            );
          })}
        </div>
      )}

      <div
        className="bg-[#BCB7A8] h-full w-full p-2 overflow-y-auto border border-black"
        ref={divRef}
      >
        {messages.map((m, i) => {
          // let showAuthor = true;
          // if (i-1 >= 0) {
          //   showAuthor = messages[i-1].author.id != m.author.id;
          // }

          return (
            <p key={i}>{m.author.playerName} ({m.author.estate}{" "}{m.author.role}): {m.msg}</p>
          )
        })}

        <div ref={listEndRef}/>
      </div>

      <div className="w-full flex flex-row gap-2">
        <input
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="w-full bg-[#BCB7A8] outline-none p-2 border border-black"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              onSendMessage();
            }
          }}
        />
        <button onClick={onSendMessage} className="bg-[#BCB7A8] border border-black px-2">Send</button>
      </div>
    </div>
  )
}
